/** The macOS wrong-password wobble — the app's ONE rejection gesture.
 *
 * WEB ANIMATIONS API, NOT A CSS CLASS, and the reason is the bug that produced
 * it. A CSS animation restarts only when its class appears on a node, so a
 * second identical answer animated nothing; forcing it with a changing React
 * `key` did restart it — by destroying and recreating the element, which reads
 * as a BLINK rather than as a wobble (operator, 2026-09-10). `element.animate()`
 * restarts on every call, touches no class, and remounts nothing.
 *
 * Honours `prefers-reduced-motion` here rather than in a stylesheet: this
 * animation never passes through CSS, so the global `.animate-shake` guard in
 * the web app cannot see it.
 *
 * IT NEVER SHIPS ALONE. A wobble says "no" and nothing else, and a viewer with
 * reduced motion on gets no wobble at all — so every caller pairs it with words
 * (a toast, or an inline line) that survive without it. `HighlightOptionGroup`
 * does; so does the import page's add-to-search CTA.
 *
 * LIFTED OUT OF `HighlightOptionGroup` (2026-09-15) when the second caller
 * arrived. The amplitudes below were tuned by watching it, and a copy would
 * have drifted from them on the first adjustment.
 */
export function runShake(el: HTMLElement | null | undefined): void {
  if (!el || typeof el.animate !== "function") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  el.animate(
    [
      // Amplitudes raised 15% on 2026-09-10 after the operator watched it run:
      // the wobble read as correct but too polite to be a rejection. The RATIO
      // between the two pairs is what keeps it a macOS shake rather than a
      // vibration, so both scaled together — 6→6.9 and 4→4.6.
      { transform: "translateX(0)" },
      { transform: "translateX(-6.9px)", offset: 0.2 },
      { transform: "translateX(6.9px)", offset: 0.4 },
      { transform: "translateX(-4.6px)", offset: 0.6 },
      { transform: "translateX(4.6px)", offset: 0.8 },
      { transform: "translateX(0)" },
    ],
    { duration: 400, easing: "cubic-bezier(0.36, 0.07, 0.19, 0.97)" }
  );
}
